import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AlertTriangle, MessageSquare, Lock, CheckCircle2, Video, Fingerprint, Camera } from 'lucide-react';
import api from '../../utils/api';
import { connectSocket } from '../../utils/socket';
import toast from 'react-hot-toast';
import useEscapeKey from '../../hooks/useEscapeKey';
import { Avatar, Select, Modal, Button, Textarea, LoadingState, EmptyState, Card } from '../../components/ui';

const statusConfig = {
  ACTIVE: { cls: 'bg-green-100 text-green-700 border-green-200', dot: 'bg-green-500 animate-pulse', label: 'Active' },
  DISCONNECTED: { cls: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500', label: 'Disconnected' },
  WARNING: { cls: 'bg-yellow-100 text-yellow-700 border-yellow-200', dot: 'bg-yellow-500 animate-pulse', label: 'Warning Issued' },
  TAB_SWITCH: { cls: 'bg-orange-100 text-orange-700 border-orange-200', dot: 'bg-orange-500 animate-pulse', label: 'Tab Switched' },
  SUBMITTED: { cls: 'bg-slate-100 text-slate-600 border-slate-200', dot: 'bg-slate-400', label: 'Submitted' },
  FORCE_SUBMITTED: { cls: 'bg-slate-100 text-slate-600 border-slate-200', dot: 'bg-slate-400', label: 'Force Submitted' },
  LOCKED: { cls: 'bg-purple-100 text-purple-700 border-purple-200', dot: 'bg-purple-500', label: 'Locked' },
};

const formatTime = (seconds) => {
  if (!seconds || seconds <= 0) return '00:00';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const StudentCard = ({ student, onAction }) => {
  const status = student.status || 'ACTIVE';
  const cfg = statusConfig[status] || statusConfig.ACTIVE;
  const progress = student.totalQuestions > 0
    ? Math.round((student.answeredCount / student.totalQuestions) * 100)
    : null;

  return (
    <div className={`bg-white rounded-xl border-2 ${cfg.cls} shadow-sm p-4 flex flex-col gap-3`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <Avatar name={student.name} size="sm" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900 truncate">{student.name}</p>
            <p className="text-xs text-slate-500 truncate">{student.username}</p>
          </div>
        </div>
        <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-xs font-medium flex-shrink-0 ${cfg.cls}`}>
          <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
          {cfg.label}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-slate-50 rounded-lg px-3 py-2 text-center">
          <p className="text-lg font-bold text-red-600">{student.violationCount || 0}</p>
          <p className="text-xs text-slate-500">Violations</p>
        </div>
        <div className="bg-slate-50 rounded-lg px-3 py-2 text-center">
          <p className={`text-lg font-bold ${student.timeRemaining > 0 ? 'text-themePrimary' : 'text-slate-400'}`}>
            {student.timeRemaining > 0 ? formatTime(student.timeRemaining) : '—'}
          </p>
          <p className="text-xs text-slate-500">Time Left</p>
        </div>
      </div>

      {/* Exam progress */}
      {progress !== null && (
        <div>
          <div className="flex justify-between text-xs text-slate-500 mb-1">
            <span>Progress</span>
            <span className="font-medium">{student.answeredCount}/{student.totalQuestions} answered</span>
          </div>
          <div className="w-full bg-slate-100 rounded-full h-1.5">
            <div className="bg-themePrimary h-1.5 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}

      {/* Last violation */}
      {student.lastViolation && (
        <p className="flex items-center gap-1.5 text-xs text-orange-600 bg-orange-50 border border-orange-100 rounded-lg px-2.5 py-1.5 leading-snug truncate" title={student.lastViolation}>
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" /> {student.lastViolation}
        </p>
      )}

      {/* Actions */}
      <div className="grid grid-cols-2 gap-1.5">
        <button
          onClick={() => onAction('warn', student)}
          className="flex items-center justify-center gap-1 text-xs font-medium py-1.5 px-2 rounded-lg bg-yellow-50 hover:bg-yellow-100 text-yellow-700 border border-yellow-200 transition-colors"
        >
          <AlertTriangle className="w-3.5 h-3.5" /> Warn
        </button>
        <button
          onClick={() => onAction('message', student)}
          className="flex items-center justify-center gap-1 text-xs font-medium py-1.5 px-2 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 transition-colors"
        >
          <MessageSquare className="w-3.5 h-3.5" /> Message
        </button>
        <button
          onClick={() => onAction('lock', student)}
          className="flex items-center justify-center gap-1 text-xs font-medium py-1.5 px-2 rounded-lg bg-orange-50 hover:bg-orange-100 text-orange-700 border border-orange-200 transition-colors"
        >
          <Lock className="w-3.5 h-3.5" /> Lock
        </button>
        <button
          onClick={() => onAction('forceSubmit', student)}
          className="flex items-center justify-center gap-1 text-xs font-medium py-1.5 px-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 transition-colors"
        >
          <CheckCircle2 className="w-3.5 h-3.5" /> Force Submit
        </button>
        <button
          onClick={() => onAction('monitor', student)}
          className="flex items-center justify-center gap-1 text-xs font-medium py-1.5 px-2 rounded-lg bg-themePrimary/10 hover:bg-themePrimary/20 text-themePrimary border border-themePrimary/20 transition-colors col-span-2"
        >
          <Video className="w-3.5 h-3.5" /> Monitor Webcam
        </button>
      </div>
    </div>
  );
};

const ACTION_BUTTON_CLASS = {
  forceSubmit: '!bg-red-600 hover:!bg-red-500',
  lock: '!bg-orange-600 hover:!bg-orange-500',
  warn: '!bg-yellow-600 hover:!bg-yellow-500',
  message: '',
};

const ProctorDashboard = () => {
  const [exams, setExams] = useState([]);
  const [selectedExamId, setSelectedExamId] = useState('');
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionStudent, setActionStudent] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [sendingAction, setSendingAction] = useState(false);
  const [streams, setStreams] = useState({});
  const socketRef = useRef(null);
  const [connected, setConnected] = useState(false);

  const closeActionModal = useCallback(() => {
    setActionStudent(null);
    setActionType(null);
  }, []);
  useEscapeKey(closeActionModal, !!actionStudent);

  useEffect(() => {
    const fetchExams = async () => {
      try {
        const res = await api.get('/proctor/exams');
        const list = res.data.data?.exams || [];
        setExams(list);
        if (list.length > 0) setSelectedExamId(list[0]._id || list[0].id);
      } catch (err) {
        toast.error('Failed to load exams');
      }
    };
    fetchExams();
  }, []);

  useEffect(() => {
    if (!selectedExamId) return;
    const fetchStudents = async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        const res = await api.get(`/exams/${selectedExamId}/students`);
        const raw = res.data.data || [];
        const list = raw.map(item => ({
          ...(item.student || item),
          violationCount: item.attempt?.violationCount || 0,
          status: item.attempt
            ? (['SUBMITTED', 'FORCE_SUBMITTED', 'LOCKED'].includes(item.attempt.status)
              ? item.attempt.status
              : 'ACTIVE')
            : 'ACTIVE',
          attemptId: item.attempt?._id,
          timeRemaining: null,
          answeredCount: undefined,
          totalQuestions: undefined,
          lastViolation: null,
        }));
        setStudents(prev => {
          if (silent && prev.length > 0) {
            return list.map(fresh => {
              const existing = prev.find(p => (p._id || p.id)?.toString() === (fresh._id || fresh.id)?.toString());
              if (!existing) return fresh;
              const liveStatuses = ['WARNING', 'TAB_SWITCH', 'DISCONNECTED'];
              return {
                ...fresh,
                timeRemaining: existing.timeRemaining,
                answeredCount: existing.answeredCount,
                totalQuestions: existing.totalQuestions,
                lastViolation: existing.lastViolation,
                status: liveStatuses.includes(existing.status) ? existing.status : fresh.status,
              };
            });
          }
          return list;
        });
      } catch (err) {
        if (!silent) setStudents([]);
      } finally {
        if (!silent) setLoading(false);
      }
    };
    fetchStudents();
    const pollInterval = setInterval(() => fetchStudents(true), 15000);
    return () => clearInterval(pollInterval);
  }, [selectedExamId]);

  useEffect(() => {
    if (!selectedExamId) return;

    const socket = connectSocket();
    socketRef.current = socket;
    setConnected(socket.connected);

    const joinRoom = () => {
      setConnected(socket.connected);
      socket.emit('join-proctor', { examId: selectedExamId });
    };
    joinRoom();

    socket.on('connect', () => { setConnected(true); socket.emit('join-proctor', { examId: selectedExamId }); });
    socket.on('disconnect', () => setConnected(false));

    socket.on('student-status', (data) => {
      const sid = data.studentId?.toString();
      setStudents(prev =>
        prev.map(s => {
          if ((s._id || s.id)?.toString() !== sid) return s;
          const updates = {};
          if (data.status === 'CONNECTED') {
            updates.status = 'ACTIVE';
          } else if (data.status === 'DISCONNECTED') {
            updates.status = 'DISCONNECTED';
          }
          if (data.data?.timeRemaining !== undefined) updates.timeRemaining = data.data.timeRemaining;
          if (data.data?.answeredCount !== undefined) updates.answeredCount = data.data.answeredCount;
          if (data.data?.totalQuestions !== undefined) updates.totalQuestions = data.data.totalQuestions;
          return { ...s, ...updates };
        })
      );
    });

    socket.on('student_stream', (data) => {
      setStreams(prev => ({ ...prev, [data.studentId?.toString()]: data }));
    });

    socket.on('violation', (data) => {
      const isTabSwitch = data.type === 'TAB_SWITCH' || data.type === 'WINDOW_BLUR';
      toast.custom(
        (t) => (
          <div className={`${t.visible ? 'animate-enter' : 'animate-leave'} max-w-sm w-full bg-white shadow-lg rounded-xl border-l-4 ${isTabSwitch ? 'border-orange-500' : 'border-red-500'} p-4`}>
            <p className={`text-sm font-bold ${isTabSwitch ? 'text-orange-700' : 'text-red-700'}`}>
              {isTabSwitch ? 'Tab Switch Detected' : 'Violation Detected'}
            </p>
            <p className="text-sm text-slate-700 mt-0.5">
              <span className="font-medium">{data.studentName}</span>: {data.description}
            </p>
          </div>
        ),
        { duration: 5000 }
      );

      setStudents(prev =>
        prev.map(s => {
          if ((s._id || s.id)?.toString() !== data.studentId?.toString()) return s;
          const newStatus = isTabSwitch ? 'TAB_SWITCH' : 'WARNING';
          const finalStatus = s.status === 'WARNING' ? 'WARNING' : newStatus;
          return { ...s, violationCount: (s.violationCount || 0) + 1, status: finalStatus, lastViolation: data.description };
        })
      );
    });

    return () => {
      socket.off('student-status');
      socket.off('student_stream');
      socket.off('violation');
      socket.off('connect');
      socket.off('disconnect');
    };
  }, [selectedExamId]);

  const handleAction = (type, student) => {
    setActionType(type);
    setActionStudent(student);
    setMessageText('');
  };

  const executeAction = async () => {
    if (!actionStudent || !actionType) return;
    setSendingAction(true);
    const studentId = actionStudent._id || actionStudent.id;
    const attemptId = actionStudent.attemptId;
    try {
      if (actionType === 'warn') {
        await api.post(`/proctor/warn`, { examId: selectedExamId, studentId, attemptId });
        if (socketRef.current) {
          socketRef.current.emit('send-message', {
            examId: selectedExamId,
            recipientId: studentId.toString(),
            content: 'Warning: You have been issued a formal warning by the proctor.',
            type: 'WARNING'
          });
        }
        toast.success(`Warning sent to ${actionStudent.name}`);
      } else if (actionType === 'message') {
        if (!messageText.trim()) { toast.error('Enter a message'); setSendingAction(false); return; }
        await api.post('/proctor/message', { examId: selectedExamId, studentId, attemptId, content: messageText });
        if (socketRef.current) {
          socketRef.current.emit('send-message', {
            examId: selectedExamId,
            recipientId: studentId.toString(),
            content: messageText,
            type: 'INFO'
          });
        }
        toast.success('Message sent');
      } else if (actionType === 'lock') {
        await api.post(`/proctor/lock`, { examId: selectedExamId, studentId, attemptId });
        if (socketRef.current) {
          socketRef.current.emit('lock-student', { examId: selectedExamId, studentId: studentId.toString() });
        }
        toast.success(`Exam locked for ${actionStudent.name}`);
      } else if (actionType === 'forceSubmit') {
        await api.post(`/proctor/force-submit`, { examId: selectedExamId, studentId, attemptId });
        if (socketRef.current) {
          socketRef.current.emit('force-submit-student', { examId: selectedExamId, studentId: studentId.toString() });
        }
        setStudents(prev => prev.map(s => (s._id || s.id) === studentId ? { ...s, status: 'SUBMITTED' } : s));
        toast.success(`Exam force-submitted for ${actionStudent.name}`);
      }
      setActionStudent(null);
      setActionType(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Action failed');
    } finally {
      setSendingAction(false);
    }
  };

  const MESSAGE_TEMPLATES = [
    'Return to the exam window immediately.',
    'Tab switching has been detected. This is a warning.',
    'Final warning: Next violation will result in exam termination.',
    'Please stay in fullscreen mode.'
  ];

  const activeCount = students.filter(s => s.status === 'ACTIVE').length;
  const warningCount = students.filter(s => s.status === 'WARNING' || s.status === 'TAB_SWITCH').length;
  const disconnectedCount = students.filter(s => s.status === 'DISCONNECTED').length;
  const submittedCount = students.filter(s => s.status === 'SUBMITTED' || s.status === 'FORCE_SUBMITTED').length;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-slate-900">Proctor Dashboard</h2>
          <div className="flex items-center gap-2 mt-1">
            <div className={`w-2 h-2 rounded-full ${connected ? 'bg-green-500' : 'bg-red-400'}`} />
            <p className="text-sm text-slate-500">{connected ? 'Connected' : 'Disconnected'}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-slate-700 flex-shrink-0">Exam:</label>
          <Select value={selectedExamId} onChange={e => setSelectedExamId(e.target.value)} className="min-w-48">
            <option value="">Select an exam</option>
            {exams.map(e => <option key={e._id || e.id} value={e._id || e.id}>{e.name}</option>)}
          </Select>
        </div>
      </div>

      {/* Stats Row */}
      {selectedExamId && (
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
          {[
            { label: 'Total', value: students.length, color: 'text-themePrimary' },
            { label: 'Active', value: activeCount, color: 'text-green-600' },
            { label: 'Violations', value: warningCount, color: 'text-yellow-600' },
            { label: 'Disconnected', value: disconnectedCount, color: 'text-red-600' },
            { label: 'Submitted', value: submittedCount, color: 'text-slate-500' },
          ].map(({ label, value, color }) => (
            <Card key={label} padding="p-4" className="text-center">
              <p className={`text-2xl font-bold ${color}`}>{value}</p>
              <p className="text-xs text-slate-500 mt-1">{label}</p>
            </Card>
          ))}
        </div>
      )}

      {/* Student Grid */}
      {selectedExamId ? (
        loading ? (
          <LoadingState label="Loading students..." />
        ) : students.length === 0 ? (
          <Card><EmptyState title="No students in this session" description="Students will appear here once they join the exam." /></Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {students.map(s => (
              <StudentCard key={s._id || s.id || s.attemptId} student={s} onAction={handleAction} />
            ))}
          </div>
        )
      ) : (
        <Card><EmptyState title="Select an exam" description="Choose an exam above to start monitoring students." /></Card>
      )}

      {/* Action Modal */}
      <Modal
        open={!!(actionStudent && actionType)}
        onClose={closeActionModal}
        title={actionStudent ? `${actionType === 'forceSubmit' ? 'Force Submit' : actionType}: ${actionStudent.name}` : ''}
        footer={
          actionType === 'monitor' ? (
            <Button variant="secondary" onClick={closeActionModal}>Close</Button>
          ) : (
            <>
              <Button variant="outline" onClick={closeActionModal}>Cancel</Button>
              <Button
                variant="primary"
                loading={sendingAction}
                onClick={executeAction}
                className={ACTION_BUTTON_CLASS[actionType] || ''}
              >
                Confirm
              </Button>
            </>
          )
        }
      >
        {actionType === 'message' && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Quick Templates</label>
              <div className="space-y-2">
                {MESSAGE_TEMPLATES.map((t, i) => (
                  <button
                    key={i}
                    onClick={() => setMessageText(t)}
                    className={`w-full text-left text-xs px-3 py-2 rounded-lg border transition-colors ${messageText === t ? 'border-themePrimary bg-themePrimary/5 text-themePrimary' : 'border-slate-200 hover:border-themePrimary/40 text-slate-600'}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <Textarea
              label="Custom Message"
              value={messageText}
              onChange={e => setMessageText(e.target.value)}
              rows={3}
              placeholder="Type a custom message..."
            />
          </div>
        )}

        {actionType === 'warn' && (
          <p className="text-sm text-slate-600">
            Send a formal warning to <strong>{actionStudent?.name}</strong>. This will appear as a notification in their exam.
          </p>
        )}

        {actionType === 'lock' && (
          <p className="text-sm text-slate-600">
            Lock the exam for <strong>{actionStudent?.name}</strong>. They will not be able to continue until unlocked.
          </p>
        )}

        {actionType === 'forceSubmit' && (
          <p className="text-sm text-slate-600">
            Force-submit the exam for <strong>{actionStudent?.name}</strong>. Their current answers will be saved and submitted immediately.
          </p>
        )}

        {actionType === 'monitor' && actionStudent && (() => {
          const sid = (actionStudent._id || actionStudent.id)?.toString();
          const snap = streams[sid];
          const capturedAt = snap?.capturedAt ? new Date(snap.capturedAt).toLocaleTimeString() : null;
          return (
            <div className="space-y-4">
              <p className="flex items-center gap-1.5 text-xs text-slate-500">
                {capturedAt ? (
                  <>
                    {snap?.isInitialSnapshot ? <Fingerprint className="w-3.5 h-3.5" /> : <Camera className="w-3.5 h-3.5" />}
                    {snap?.isInitialSnapshot ? 'Identity verification snapshot' : 'Last snapshot'}: {capturedAt}
                  </>
                ) : (
                  'Webcam snapshots taken every 60 seconds. First snapshot on exam start.'
                )}
              </p>
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Webcam</h4>
                <div className="bg-black aspect-video rounded-lg overflow-hidden flex items-center justify-center border border-slate-200">
                  {snap?.webcamFrame ? (
                    <img src={snap.webcamFrame} alt="Webcam" className="w-full h-full object-contain" />
                  ) : (
                    <div className="text-center px-4">
                      <p className="text-slate-400 text-xs">No snapshot yet</p>
                      <p className="text-slate-500 text-xs mt-1">First snapshot arrives after 60s</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
};

export default ProctorDashboard;
